/**
 * Public magic-link login page.
 *
 * Single-purpose: type your email → 202 → "check your inbox". The
 * magic link in the email lands at /auth/consume which the worker
 * handles directly (sets cookie, redirects to /).
 *
 * Restrained, airy layout per the design's portal aesthetic
 * (Decisions #14 — client surfaces are airy, not dense).
 */

import { useState, type FormEvent } from 'react';
import { Mail } from 'lucide-react';

import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';

type Stage = 'enter' | 'sent' | 'error';

export function Login() {
  const [email, setEmail] = useState('');
  const [stage, setStage] = useState<Stage>('enter');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!email.trim() || pending) return;
    setPending(true);
    setError(null);
    try {
      const res = await fetch('/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      });
      if (res.ok) {
        setStage('sent');
      } else {
        setStage('error');
        setError('Something went wrong. Try again in a moment.');
      }
    } catch {
      setStage('error');
      setError('Network error. Check your connection and try again.');
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-screen w-full bg-ink-900 flex items-center justify-center p-6">
      <div className="w-full max-w-[420px] space-y-6">
        <div className="text-center space-y-2">
          <div className="text-[28px] font-semibold tracking-tight text-ink-100">
            Beacon
          </div>
          <div className="text-[13px] text-ink-300">
            Conduct HQ — sign in to continue
          </div>
        </div>

        {stage === 'enter' && (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="text-[12px] uppercase tracking-[0.08em] text-ink-300 font-semibold">
                Email
              </span>
              <Input
                type="email"
                autoComplete="email"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@conducthq.com"
                className="mt-1.5 h-10 text-[14px]"
              />
            </label>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={pending || !email.trim()}
              className="w-full justify-center"
            >
              <Mail size={14} />
              {pending ? 'Sending…' : 'Send magic link'}
            </Button>
            <p className="text-[11.5px] text-ink-400 mono leading-relaxed">
              We&apos;ll email you a one-time sign-in link, valid 15 minutes.
            </p>
          </form>
        )}

        {stage === 'sent' && (
          <div className="bg-ink-800 border border-ink-600 rounded-[10px] p-5 space-y-2">
            <div className="text-[14px] font-semibold text-ink-100">
              Check your inbox
            </div>
            <div className="text-[13px] text-ink-300 leading-relaxed">
              We sent a sign-in link to{' '}
              <span className="text-ink-100 mono">{email}</span>. Click it
              to continue. The link expires in 15 minutes.
            </div>
            <button
              onClick={() => {
                setStage('enter');
                setEmail('');
              }}
              className="text-[12px] text-beacon-300 hover:underline focus-ring rounded"
            >
              Use a different email
            </button>
          </div>
        )}

        {stage === 'error' && (
          <div className="bg-danger-500/10 border border-danger-500/30 rounded-[10px] p-4 space-y-2">
            <div className="text-[13px] text-danger-400 font-semibold">
              Couldn&apos;t send link
            </div>
            <div className="text-[12.5px] text-ink-200">{error}</div>
            <button
              onClick={() => setStage('enter')}
              className="text-[12px] text-beacon-300 hover:underline focus-ring rounded"
            >
              Try again
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
